import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { Accordion, Flex, Divider, Icon, Menu } from "@theorgchartdev/storybook";
import { LayoutSegment } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/LayoutSegment";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import cx from "classnames";
import { useEffect, useRef } from "react";
import { BuildInsertOptions } from "@/appComponents/Navigation/ContextualToolbar/InsertMenu/InsertMenu";
import { GetBGMgr, GetWSMgr } from "@/Utils/StateLib";
import { handleImageSelect } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/LayoutControls";
import { appstr } from "@coreapp/utils/appstr";
import { enumBoxSubTypes, kAutoTextStrs, enumFileMgrMode } from "@coreapp/constants";

export const AreaAccordion = props => {
  const {
    name,
    backgroundArea,
    area,
    index,
    accordionHandle,
    accordionHandleRef,
    handleLayoutChange,
    handleLayoutAdd,
    handleLayoutRemove,
    handleExpandArea,
    handleCollapseArea
  } = props;

  const openCtrlRef = useRef();
  const targetArea = backgroundArea && area ? backgroundArea[area] : null;
  const layouts = targetArea?.GetLayouts() ?? [];
  const sortableItems = layouts.map((_, layoutIndex) => ({
    id: `layout-${area}-${layoutIndex}`
  }));

  const { setNodeRef } = useDroppable({
    id: `area-${area}`,
    data: { area: area, layoutIndex: sortableItems?.length - 1 }
  });

  useEffect(() => {
    const collapse = emptyArea();
    if (collapse) {
      handleCollapseArea(area);
    } else {
      handleExpandArea(area);
    }
  }, [name]);

  const doHandleAdd = layout => {
    handleExpandArea(area);
    handleLayoutAdd(area, layout);
  };

  const emptyArea = () => {
    const bgMgr = GetBGMgr();
    const activeBackground = bgMgr?.fActiveBackgroundArea;
    if (!activeBackground) return true;
    const activeAreas = activeBackground.GetAreas();
    const targetArea = activeAreas[area];
    const layouts = targetArea?.GetLayouts() ?? [];
    const contentLen = layouts?.length;
    return contentLen === 0;
  };

  const handleAddLayout = o => {
    const wsMgr = GetWSMgr();
    let options = {};
    if (o.value === enumBoxSubTypes.picture) {
      wsMgr.DoFileMgr(enumFileMgrMode.selectimage, {
        customOnSelect: (dir, file) => handleImageSelect(dir, file, doHandleAdd, area)
      });
      return;
    }
    let newLayout;
    let autoTextIndex = kAutoTextStrs.indexOf(o.value);
    if (autoTextIndex > -1) {
      newLayout = wsMgr.GetAutoTextLayout(autoTextIndex);
    } else {
      newLayout = wsMgr.GetNewLayoutByBoxSubType(o.value, options);
    }
    doHandleAdd(newLayout);
  };

  const checkAccordionActive = () => {
    const ctrl = accordionHandle.get(area);
    return openCtrlRef?.current?.open || ctrl?.isExpanded;
  };

  const addTrigger = (
    <Icon
      name={"plus"}
      size={"small"}
      layout={"default"}
      tooltipProps={{ message: appstr("Add element") }}
      onClick={e => {
        handleExpandArea(area);
        openCtrlRef?.current?.setOpen(!openCtrlRef?.current?.open);
        e.stopPropagation();
      }}
    />
  );

  const areaEmpty = emptyArea();

  // Custom placement area is dedicated to legacy backgrounds
  if (backgroundArea == null || (area === "customPlacement" && areaEmpty)) return;

  return (
    <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
      <Flex
        ref={setNodeRef}
        direction={"column"}
        gap={"8px"}
        className={cx("sidebar-content-item", index > 0 ? "sidebar-content-item--divider" : null)}
      >
        <Accordion
          initExpand={!areaEmpty}
          handleRef={accordionHandleRef}
          dataTestId={`${area}-accordion`}
          name={
            <ActionListItem
              padding={"0px"}
              label={appstr(area)}
              labelProps={{ modifier: "bold", size: "small" }}
              actions={
                area !== "customPlacement"
                  ? [
                      {
                        checkActive: checkAccordionActive,
                        content: (
                          <Menu
                            dataTestId={`${area}-add-btn`}
                            referenceProps={{ ref: openCtrlRef, onClick: e => e.stopPropagation() }}
                            openCtrlRef={openCtrlRef}
                            fillWidth={false}
                            trigger={addTrigger}
                            options={BuildInsertOptions({
                              clickHandler: handleAddLayout,
                              testId: `${area}-add-option`
                            })}
                            position={"bottom-end"}
                            optionSize={"small"}
                            listWidth={"medium"}
                            disabled={area === "customPlacement"}
                            popoverProps={{
                              floatingProps: { "data-testid": "background-elements-insert-popover" }
                            }}
                            listProps={{
                              nestedPopoverProps: {
                                floatingProps: {
                                  "data-testid": "background-elements-insert-subpopover"
                                }
                              }
                            }}
                          />
                        )
                      }
                    ]
                  : undefined
              }
              buttonsInHierarchy
            />
          }
          beforeContent={
            <Flex direction={"column"} margin={"0 0 0 12px"} gap={"12px"}>
              {layouts.map((layout, index) => (
                <Flex key={index} direction={"column"} gap={"12px"} width={"100%"}>
                  <LayoutSegment
                    area={area}
                    layoutIndex={index}
                    layout={layout}
                    setLayout={handleLayoutChange(index)}
                    handleLayoutRemove={handleLayoutRemove(index)}
                  />
                  {layouts.length > 1 && index < layouts.length - 1 && <Divider vertical={false} />}
                </Flex>
              ))}
            </Flex>
          }
        />
      </Flex>
    </SortableContext>
  );
};
